import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Typography,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  CircularProgress,
  Alert,
  TextField,
  IconButton,
} from '@mui/material';
import Pagination from '@mui/material/Pagination';
import DeleteIcon from '@mui/icons-material/Delete';
import { useNavigate } from 'react-router-dom';
import api from '../../api/axios';

interface Tyre {
  _id: string;
  name: string;
  brand: string;
  model: string;
  type: string;
  width: string;
  height: string;
  diameter: string;
  price: number;
  price_rrp?: number;
  stock?: number;
  source?: string;
  isMy?: boolean;
  bestseller?: boolean;
}

const TyreRegistryPage = () => {
  const [tyres, setTyres] = useState<Tyre[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [totalPages, setTotalPages] = useState(1);

  const [prioritizeMy, setPrioritizeMy] = useState(false);

  const [bestseller, setBestseller] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    const fetchTyres = async () => {
      try {
        setLoading(true);
        const response = await api.get('/api/admin/tyre', {
          params: {
            page,
            limit,
            search: searchQuery,
            prioritizeMy,
            bestseller,
          },
        });
        setTyres(response.data.data);
        setTotalPages(response.data.pages);
      } catch (err) {
        setError('Ошибка при загрузке шин.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchTyres();
  }, [page, searchQuery, prioritizeMy, bestseller]);

  const handleAddClick = () => {
    navigate('/tyre/create');
  };

  const handleDelete = async (tyreId: string) => {
    if (!window.confirm('Вы уверены, что хотите удалить эту шину?')) return;

    try {
      await api.delete(`/api/admin/tyre/${tyreId}`);
      setTyres((prev) => prev.filter((t) => t._id !== tyreId));
    } catch (err) {
      console.error('Ошибка при удалении шины:', err);
      setError('Не удалось удалить шину.');
    }
  };

  return (
    <Box p={4}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5">Реестр шин</Typography>
        <Button variant="contained" color="primary" onClick={handleAddClick}>
          Добавить шину
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box
        display="flex"
        alignItems="center"
        gap={3}
        flexWrap="wrap"
        sx={{ mb: 2 }}
      >
        <TextField
          label="Поиск по названию"
          variant="outlined"
          size="small"
          value={searchQuery}
          onChange={(e) => {
            setPage(1);
            setSearchQuery(e.target.value);
          }}
          sx={{ width: '300px' }}
        />

        <Box display="flex" alignItems="center">
          <input
            type="checkbox"
            id="prioritizeMyTyres"
            checked={prioritizeMy}
            onChange={(e) => {
              setPage(1);
              setPrioritizeMy(e.target.checked);
            }}
          />
          <label
            htmlFor="prioritizeMyTyres"
            style={{ marginLeft: 8, fontSize: 14 }}
          >
            Сначала мои шины
          </label>
        </Box>
        <Box display="flex" alignItems="center">
          <input
            type="checkbox"
            id="bestsellerTyres"
            checked={bestseller}
            onChange={(e) => {
              setPage(1);
              setBestseller(e.target.checked);
            }}
          />
          <label
            htmlFor="bestsellerTyres"
            style={{ marginLeft: 8, fontSize: 14 }}
          >
            Сначала хиты продаж
          </label>
        </Box>
      </Box>

      {loading ? (
        <CircularProgress />
      ) : tyres.length === 0 ? (
        <Typography>Список шин пуст.</Typography>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>№</TableCell>
                <TableCell>Наименование</TableCell>
                <TableCell>Бренд</TableCell>
                <TableCell>Модель</TableCell>
                <TableCell>Тип</TableCell>
                <TableCell>Размер</TableCell>
                <TableCell>Цена</TableCell>
                <TableCell>Источник</TableCell>
                <TableCell>Остаток</TableCell>
                <TableCell>Хит продаж</TableCell>
                <TableCell align="right">Действия</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tyres.map((tyre, index) => (
                <TableRow
                  key={tyre._id}
                  hover
                  onClick={() => navigate(`/tyre/${tyre._id}`)}
                  sx={{
                    cursor: 'pointer',
                    backgroundColor: tyre.isMy ? '#8ce78fff' : 'inherit',
                  }}
                >
                  <TableCell>{(page - 1) * limit + index + 1}</TableCell>
                  <TableCell>{tyre.name}</TableCell>
                  <TableCell>{tyre.brand}</TableCell>
                  <TableCell>{tyre.model}</TableCell>
                  <TableCell>{tyre.type}</TableCell>
                  <TableCell>
                    {tyre.width && tyre.height && tyre.diameter
                      ? `${tyre.width}/${tyre.height}R${tyre.diameter}`
                      : '—'}
                  </TableCell>
                  <TableCell>
                    {Number(tyre.price)
                      ? `${Number(tyre.price).toLocaleString()} ₽`
                      : '—'}
                  </TableCell>
                  <TableCell>{tyre.source ?? '—'}</TableCell>
                  <TableCell>
                    {typeof tyre.stock === 'number' ? tyre.stock : '—'}
                  </TableCell>
                  <TableCell>{tyre.bestseller ? 'Да' : 'Нет'}</TableCell>
                  <TableCell
                    align="right"
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                  >
                    <IconButton
                      color="error"
                      onClick={() => handleDelete(tyre._id)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Box display="flex" justifyContent="center" mt={3}>
        <Pagination
          count={totalPages}
          page={page}
          onChange={(_, value) => setPage(value)}
          color="primary"
        />
      </Box>
    </Box>
  );
};

export default TyreRegistryPage;
